import type { PaginatedResultsResponse } from "../types/generic.types";
import type {
  AccessAttempt,
  AccessAttemptsResult,
  AdminSettingsActivityLog,
  AdminSettingsActivityLogResult,
  AdminSettingsActivityLogsResponse,
  BadgeDefinition,
  BadgeResponse,
  ConstanceConfigs,
  ConstanceConfigsResult,
  CreateBadgePayload,
  CreateIntegrationConfigParams,
  IntegrationConfig,
  IntegrationConfigResponse,
  IntegrationToken,
  IntegrationTokenParams,
  IntegrationTokenResponse,
  IntegrationTokensListResponse,
  TaskLog,
  TaskLogResult,
  UpdateBadgePayload,
  UpdateIntegrationConfigParams,
  UserConfig,
  UserConfigCreateParams,
  UserConfigResponse,
  UserConfigUpdateParams
} from "../types/instanceSettings.types";
import { arrayToHashmap } from "@certa/common/utils/array";
import isEmpty from "lodash-es/isEmpty";

export const integrationTokensListModelCreator = (
  response: IntegrationTokensListResponse[]
) => response.map(it => ({ id: it.id, tokenType: it.token_type }));

export const integrationTokenModelCreator = (
  result: IntegrationTokenResponse
): IntegrationToken => ({
  id: result.id,
  tokenType: result.token_type,
  isStatic: result.is_static,
  username: result.username || undefined,
  password: result.password || undefined,
  tokenURL: result.token_url || undefined,
  tokenHeaders: result.token_headers || undefined,
  tokenBody: result.token_body || undefined,
  tokenJSONPath: result.token_json_path || undefined,
  freshTokenRequired: result.fresh_token_required,
  token: result.token || undefined,
  refreshInterval: result.refresh_interval,
  clientCertificatePrivate: result.client_certificate_private || undefined,
  lastRefreshed: result.last_refreshed,
  createdAt: result.created_at,
  updateAt: result.update_at
});

export const integrationTokenPayloadCreator = (props: IntegrationToken) => {
  const integrationPayload: IntegrationTokenParams = {
    token_type: props.tokenType,
    is_static: props.isStatic ?? false,
    username: props.username ?? "",
    password: props.password ?? "",
    token_url: props.tokenURL ?? "",
    token_headers: props.tokenHeaders ?? "",
    token_body: props.tokenBody ?? "",
    token_json_path: props.tokenJSONPath ?? "",
    fresh_token_required: props.freshTokenRequired ?? false,
    token: props.token ?? "",
    refresh_interval: props.refreshInterval ?? null,
    client_certificate_private: props.clientCertificatePrivate ?? ""
  };
  if (props.id) {
    integrationPayload.id = props.id;
  }

  return integrationPayload;
};

export const integrationConfigsModelCreator = (
  response: IntegrationConfigResponse[]
) => response.map(integrationConfigModelCreator);

export const integrationConfigModelCreator = (
  result: IntegrationConfigResponse
): IntegrationConfig => ({
  id: result.id,
  name: result.name,
  apiType: result.api_type,
  url: result.url,
  method: result.method,
  username: result.username || undefined,
  password: result.password || undefined,
  requestBodyType: result.request_body_type || undefined,
  requestBody: result.request_body || undefined,
  advancedBodyRendering: result.advanced_body_rendering,
  requestHeader: result.request_header || undefined,
  requestThreshold: result.request_threshold,
  responseListJSONPath: result.response_list_json_path || undefined,
  responseListJMESPath: result.response_list_jmespath || undefined,
  responseListUUIDKey: result.response_list_uuid_key || undefined,
  nameMatchingParams: result.name_matching_params,
  extra: result.extra,
  fileType: result.file_type || undefined,
  fileJSONPath: result.file_json_path || undefined,
  fileNameJSONPath: result.file_name_json_path || undefined,
  childWorkflowKind: result.child_workflow_kind,
  childWorkflowRequestBody: result.child_workflow_request_body || undefined,
  clientCertificatePrivate: result.client_certificate_private || undefined,
  integrationToken: result.integration_token,
  pollingInterval: result.polling_interval,
  failureEmails: Array.isArray(result.failure_emails)
    ? result.failure_emails
    : [],
  statusJSONPath: result.status_json_path || undefined,
  createdAt: result.created_at,
  updateAt: result.update_at
});

export const integrationConfigUpdateModelCreator = (
  props: IntegrationConfig
) => {
  const integrationPayload: UpdateIntegrationConfigParams = {
    id: props.id,
    name: props.name ?? "",
    url: props.url,
    method: props.method,
    username: props.username ?? "",
    password: props.password ?? "",
    request_body_type: props.requestBodyType ?? "",
    request_body: props.requestBody ?? "",
    advanced_body_rendering: props.advancedBodyRendering ?? false,
    request_header: props.requestHeader ?? "",
    request_threshold: props.requestThreshold ?? 0,
    response_list_json_path: props.responseListJSONPath ?? "",
    response_list_jmespath: props.responseListJMESPath ?? "",
    response_list_uuid_key: props.responseListUUIDKey ?? "",
    name_matching_params: props.nameMatchingParams ?? {},
    extra: props.extra ?? {},
    file_type: props.fileType ?? "",
    file_json_path: props.fileJSONPath ?? "",
    file_name_json_path: props.fileNameJSONPath ?? "",
    child_workflow_kind: props.childWorkflowKind ?? null,
    child_workflow_request_body: props.childWorkflowRequestBody ?? "",
    client_certificate_private: props.clientCertificatePrivate ?? "",
    integration_token: props.integrationToken ?? null,
    polling_interval: props.pollingInterval ?? null,
    failure_emails: props.failureEmails ?? [],
    status_json_path: props.statusJSONPath ?? ""
  };

  return integrationPayload;
};

export const integrationConfigCreateModelCreator = (
  props: IntegrationConfig
) => {
  const integrationPayload: CreateIntegrationConfigParams = {
    api_type: props.apiType,
    ...integrationConfigUpdateModelCreator(props)
  };

  return integrationPayload;
};

export const userConfigsModelCreatorPaginated = (
  props: PaginatedResultsResponse<UserConfigResponse>
) => ({
  ...props,
  results: props.results.map(userConfigModelCreator)
});

export const userConfigModelCreatorAll = (props: UserConfigResponse[]) =>
  props.map(userConfigModelCreator);

export const userConfigModelCreator = (
  props: UserConfigResponse
): UserConfig => ({
  id: props.id,
  email: props.email,
  firstName: props.first_name || "",
  lastName: props.last_name || "",
  isActive: props.is_active ?? true,
  kind: props.kind,
  groups: props.groups,
  staticGroups: props.static_groups || [],
  dynamicGroups: props.dynamic_groups?.map(group => group.name) || [],
  businessUnitFullAccess: props.business_unit_full_access || [],
  businessUnitReadAccess: props.business_unit_read_access || [],
  regionFullAccess: props.region_full_access || [],
  regionReadAccess: props.region_read_access || [],
  preferredLanguage: props.prefered_language
});

export const userConfigUpdatePayloadCreator = (
  props: UserConfig
): UserConfigUpdateParams => {
  const payload: UserConfigUpdateParams = {
    id: props.id,
    first_name: props.firstName || "",
    last_name: props.lastName || "",
    is_active: props.isActive,
    kind: props.kind,
    groups: props.groups || [],
    business_unit_full_access: props.businessUnitFullAccess || [],
    business_unit_read_access: props.businessUnitReadAccess || [],
    region_full_access: props.regionFullAccess || [],
    region_read_access: props.regionReadAccess || [],
    prefered_language: props.preferredLanguage
  };

  return payload;
};

export const userConfigCreatePayloadCreator = (
  props: UserConfig
): UserConfigCreateParams => {
  const payload: UserConfigCreateParams = {
    email: props.email,
    ...userConfigUpdatePayloadCreator(props)
  };

  return payload;
};

export const adminSettingsActivityLogsModelCreator = (
  response: AdminSettingsActivityLogsResponse
) => ({
  ...response,
  results: response.results.map(adminSettingsActivityLogModelCreator)
});

const adminSettingsActivityLogModelCreator = (
  result: AdminSettingsActivityLogResult
): AdminSettingsActivityLog => ({
  count: result.count,
  actionType: result.action_type,
  actionCode: result.action_code,
  actorEmail: result.actor_email,
  actorName: result.actor_name,
  objectId: result.object_id,
  objectName: result.object_name,
  objectType: result.object_type,
  tenant: result.tenant,
  requestId: result.request_id,
  sessionUserDetails: {
    id: result.session_user_details?.id,
    displayName: result.session_user_details?.display_name,
    email: result.session_user_details?.email
  },
  details: result.details,
  timestamp: result.timestamp
});

export const constanceConfigsModelCreator = (
  response: ConstanceConfigsResult
): ConstanceConfigs => {
  const configHashMap = arrayToHashmap(response, "key");

  return {
    cookieExpiry: configHashMap?.COOKIE_EXPIRY_IN_SECONDS || {},
    customUILables: configHashMap?.CUSTOM_UI_LABELS || {},
    environmentVariables: configHashMap?.ENV_VARS || {},
    groupsExcludedFromTagging:
      configHashMap?.GROUPS_EXCLUDED_FROM_TAGGING || {},
    integrationFailureEmails: configHashMap?.INTEGRATION_FAILURE_EMAILS || {},
    sftpFailureMails: configHashMap?.SFTP_FAILURE_EMAILS || {},
    supportedLanguages: configHashMap?.SUPPORTED_LANGUAGES || {},
    supportEmails: configHashMap?.SUPPORT_EMAIL || {},
    systemVariables: configHashMap?.SYS_VAR || {},
    tenantUnavailableMessage:
      configHashMap?.TENANT_UNAVAILABLE_MESSAGE_JSON || {},
    userImpersonationBlacklist:
      configHashMap?.USER_IMPERSONATION_BLACKLIST || {},
    userLockoutTemplateMsg: configHashMap?.USER_LOCKOUT_TEMPLATE_MSG || {},
    useEternityRuleEngine: configHashMap?.USE_ETERNITY_RULE_ENGINE || false,
    cycleTimeCalculationAlgorithm:
      configHashMap?.CYCLE_TIME_CALCULATION_ALGORITHM || {},
    cycleTimeTimezone: configHashMap?.CYCLE_TIME_TIMEZONE || {},
    weekendDays: configHashMap?.WEEKEND_DAYS || {}
  };
};

export const accessAttemptsModelCreator = (
  response: AccessAttemptsResult[]
): AccessAttempt[] =>
  response.map(result => ({
    id: result.id,
    attemptTime: result.attempt_time,
    failuresSinceStart: result.failures_since_start,
    httpAccept: result.http_accept,
    ipAddress: result.ip_address,
    userAgent: result.user_agent,
    username: result.username
  }));

export const taskLogModelCreator = (
  response: PaginatedResultsResponse<TaskLogResult>
): TaskLog[] =>
  response?.results?.map(result => ({
    id: result.id,
    errorFile: result.error_file,
    fileName: result.file_name,
    initiatedBy: result.initiated_by,
    status: result.status,
    taskType: result.task_type,
    uploadedFile: result.uploaded_file
  }));

export const badgeDefinitionModelCreator = (
  result: BadgeResponse
): BadgeDefinition => ({
  id: result.id,
  name: result.name,
  tag: result.tag,
  helpText: result.help_text,
  fieldTag: result.field_tag,
  uiSpecs: result.ui_specs,
  contentType: result.content_type,
  contentTypeTag: result.content_type_tag,
  conditions: result.conditions,
  createdAt: result.created_at,
  updatedAt: result.updated_at
});

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const badgeDefinitionsModelCreator = (
  response: BadgeResponse[]
): BadgeDefinition[] => response.map(badgeDefinitionModelCreator);

export const badgeDefinitionsModelCreatorPaginated = (
  response: PaginatedResultsResponse<BadgeResponse>
) => ({
  ...response,
  results: response.results.map(badgeDefinitionModelCreator)
});

export const badgeDefinitionCreatePayloadCreator = (
  props: Omit<BadgeDefinition, "id" | "createdAt" | "updatedAt">
): CreateBadgePayload => ({
  name: props.name,
  tag: props.tag,
  help_text: props.helpText ?? "",
  field_tag: props.fieldTag,
  ui_specs: props.uiSpecs,
  content_type: props.contentType,
  content_type_tag: props.contentTypeTag,
  conditions: props.conditions
});

export const badgeDefinitionUpdatePayloadCreator = (
  props: BadgeDefinition
): Omit<UpdateBadgePayload, "created_at" | "updated_at"> => ({
  id: props.id,
  name: props.name,
  tag: props.tag,
  help_text: props.helpText ?? "",
  field_tag: props.fieldTag,
  ui_specs: props.uiSpecs,
  content_type: props.contentType,
  content_type_tag: props.contentTypeTag,
  conditions: isEmpty(props.conditions) ? undefined : props.conditions
});
